import { z } from 'zod';

/**
 * #6a — Municipal out-of-app notifications (role-view council §6 awareness gap;
 * co-equal with #419). A municipal operator is frequently NOT 24/7-staffed, so an
 * in-viewport-only alert is one they never see. This is the consent + jurisdiction
 * model for jurisdiction-scoped SMS on threshold events.
 *
 * SMS is the FLOOR: it rides the cellular signaling channel and survives the
 * degraded-data case where app push fails. #6a ships the KEYSTONE PAIR of triggers
 * only — urgent_alert (RL≥3) + assist_ack — with the full pipeline (provider
 * interface + DB-atomic budget meter + double-opt-in + STOP + worker + UI).
 * river_rise + comms_degradation are staged to #6a-2.
 *
 * Compliance posture (legal-adjacent; founder confirms non-CEM/public-safety with
 * counsel — the build is compliance-conservative regardless): operational-content
 * only (ZERO marketing — that would convert it to a CEM under CASL), a sender ID on
 * every message, automatic STOP/ARRET opt-out, and consent_state gates EVERY send.
 */

/** The trigger sources — #6a shipped urgent_alert + assist_ack; #6a-2 (#464) wires
 *  river_rise + comms_degradation. */
export const NOTIFICATION_TRIGGER_SOURCES = [
  'urgent_alert',
  'assist_ack',
  'river_rise',
  'comms_degradation',
] as const;
export const NotificationTriggerSourceSchema = z.enum(NOTIFICATION_TRIGGER_SOURCES);
export type NotificationTriggerSource = z.infer<typeof NotificationTriggerSourceSchema>;

/**
 * river_rise severity band — the FLOOR class at which a rising-water page fires.
 * Ordered least→most severe (mirrors urgent_alert_min_rl's floor convention): 'watch'
 * pages on watch AND warning anomalies; 'warning' pages on warning only. Matches
 * flood_anomalies.anomaly_type. */
export const RIVER_RISE_BANDS = ['watch', 'warning'] as const;
export const RiverRiseBandSchema = z.enum(RIVER_RISE_BANDS);
export type RiverRiseBand = z.infer<typeof RiverRiseBandSchema>;

/** Double-opt-in consent state. 'pending' until a texted PIN is replied → 'opted_in';
 *  an inbound STOP/ARRET or an operator opt-out → 'opted_out'. */
export const NOTIFICATION_CONSENT_STATES = ['pending', 'opted_in', 'opted_out'] as const;
export const NotificationConsentStateSchema = z.enum(NOTIFICATION_CONSENT_STATES);
export type NotificationConsentState = z.infer<typeof NotificationConsentStateSchema>;

/** E.164: a leading '+' then 7–15 digits (no leading zero). */
export const E164_PHONE_RE = /^\+[1-9]\d{6,14}$/;

/** Minimum urgent-alert response level that fires a page (3 = the urgent floor). */
export const URGENT_ALERT_MIN_RL_DEFAULT = 3;

/**
 * The serialized subscription as returned by the API. The phone is MASKED (last-4
 * only) and the one-time PIN is NEVER serialized — the full number leaves the
 * server only to the SMS provider.
 */
export const NotificationSubscriptionSchema = z.object({
  id: z.string(),
  /** The owning operator's resolved identity (server-stamped, demo-safe). */
  operator_id: z.string(),
  /** The jurisdiction this subscription watches (nullable when only a bbox is set). */
  community_id: z.string().nullable(),
  community_name: z.string().nullable(),
  /** Dormant escape hatch — [minLon, minLat, maxLon, maxLat]. */
  jurisdiction_bbox: z.tuple([z.number(), z.number(), z.number(), z.number()]).nullable(),
  /** Masked for display, e.g. "+1 ••• ••• 1234". The full number is never serialized. */
  phone_masked: z.string(),
  urgent_alert_enabled: z.boolean(),
  urgent_alert_min_rl: z.number().int(),
  assist_ack_enabled: z.boolean(),
  /** #6a-2 (#464) — operator-settable rising-water + comms-degradation triggers. */
  river_rise_enabled: z.boolean(),
  /** The rising-water FLOOR band (nullable when river_rise is off / unset → the worker
   *  defaults to the inclusive 'watch' floor). */
  river_rise_band: RiverRiseBandSchema.nullable(),
  comms_degradation_enabled: z.boolean(),
  consent_state: NotificationConsentStateSchema,
  consent_basis: z.string().nullable(),
  consented_at: z.string().nullable(),
  opted_out_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type NotificationSubscription = z.infer<typeof NotificationSubscriptionSchema>;

/**
 * The opt-in POST body. operator_id / agency_id / consent_state / PIN are stamped
 * server-side (never the body). A subscription requires EITHER a community_id OR a
 * jurisdiction_bbox so the worker can scope the jurisdiction. consent_basis records
 * HOW consent was obtained (gate: carry the basis in the audited verb payload).
 */
export const NotificationSubscriptionCreateSchema = z
  .object({
    phone: z.string().trim().regex(E164_PHONE_RE, 'phone must be E.164, e.g. +12045551234'),
    community_id: z.string().uuid().optional(),
    jurisdiction_bbox: z.tuple([z.number(), z.number(), z.number(), z.number()]).optional(),
    urgent_alert_enabled: z.boolean().optional().default(true),
    urgent_alert_min_rl: z.number().int().min(3).max(4).optional().default(URGENT_ALERT_MIN_RL_DEFAULT),
    assist_ack_enabled: z.boolean().optional().default(true),
    /** Free-text record of how consent was captured (e.g. "operator self-service opt-in, COP"). */
    consent_basis: z.string().trim().min(1).max(500),
  })
  .refine((v) => v.community_id != null || v.jurisdiction_bbox != null, {
    message: 'a subscription needs a community_id or a jurisdiction_bbox',
  });
export type NotificationSubscriptionCreate = z.infer<typeof NotificationSubscriptionCreateSchema>;

/**
 * The PATCH body — edit per-trigger toggles or opt out. opt_out is a first-class
 * action (flips consent_state → opted_out). Phone and jurisdiction are immutable
 * after create (re-subscribe to change them — re-binds the consent).
 */
export const NotificationSubscriptionPatchSchema = z
  .object({
    urgent_alert_enabled: z.boolean().optional(),
    urgent_alert_min_rl: z.number().int().min(3).max(4).optional(),
    assist_ack_enabled: z.boolean().optional(),
    /** #6a-2 (#464) — self-enable the rising-water + comms-degradation triggers.
     *  river_rise_band is nullable so an operator can clear the band back to the
     *  inclusive 'watch' default. */
    river_rise_enabled: z.boolean().optional(),
    river_rise_band: RiverRiseBandSchema.nullable().optional(),
    comms_degradation_enabled: z.boolean().optional(),
    opt_out: z.literal(true).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'no changes supplied' });
export type NotificationSubscriptionPatch = z.infer<typeof NotificationSubscriptionPatchSchema>;

/** Mask an E.164 number to its last 4 digits for display/serialization. */
export function maskPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 4) return '••••';
  const last4 = digits.slice(-4);
  return `••• ••• ${last4}`;
}
